import React from 'react';
import { useHistory } from 'react-router-dom';
import { IconBtn, UiSelect } from '@ru-digital-assets/ui-component';
import { createHash } from 'crypto-pro';
import SignIcon from '../../assets/icons/sign-icon';
import FormFooter from '../../containers/form-footer';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import { useAppDispatch } from '../../store';
import { postRedeem } from '../../store/actions/operations-sign';
import { ORDER_DETAILED_STATUS, SORT_DIRECTION } from '../../types';
import OrderRealizationsDocs from './order-realizations-docs/order-realizations-docs';
import OrderRealizationsFooter from './order-realizations-footer/order-realizations-footer';
import OrderRealizationsHeader from './order-realizations-header/order-realizations-header';
import OrderRealizationsList from './order-realizations-list/order-realizations-list';
import OrderRealizationsSorts from './order-realizations-sorts/order-realizations-sorts';
import { OrderRealizationsItemProps } from './types';
import { useSign } from 'hooks';
import './order-realizations.scss';

interface OrderRealizationsProps {
  data: OrderRealizationsItemProps[];
  orderRefNumber: string;
  info?: boolean;
  status?: ORDER_DETAILED_STATUS;
  sum?: number;
  onSpecDownload: () => void;
  onSubmit: () => void;
  isInitiator: boolean;
  directions: {
    count: SORT_DIRECTION;
    sum: SORT_DIRECTION;
  };
  onToggleSort: (
    name: 'positions' | 'sum' | 'count',
    direction: SORT_DIRECTION
  ) => void;
  onClose: () => void;
  isError: boolean;
  tokenId?: string;
  initiatorId?: number;
}

const OrderRealizations: React.FC<OrderRealizationsProps> = ({
  data,
  onClose,
  onToggleSort,
  directions,
  onSpecDownload,
  onSubmit,
  status,
  sum,
  isInitiator,
  orderRefNumber,
  isError,
  tokenId,
  initiatorId,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const getFooterShowingStatus = () =>
    (isInitiator && status === 'UCP_ISSUING_REQUIRED') ||
    (!isInitiator && status === 'UCP_PAYMENT_REQUIRED');

  const {
    certificatesList,
    selectedCertificate,
    signEntity,
    onUserSelectCertificate,
  } = useSign();

  const handleSubmit = async () => {
    const bufferHash = await createHash(JSON.stringify({ tokenId }));
    const { pdfSigned } = await signEntity(bufferHash);
    if (pdfSigned && initiatorId && tokenId)
      dispatch(
        postRedeem({
          id: tokenId,
          signature: pdfSigned,
          initiatorId,
        })
      ).then(() =>
        history.push('/assets/result', {
          title: `Товарные токены успешно погашены`,
          titlePage: 'Погашение товарного токена',
          description: 'Товар отгружен. Товарный токен успешно погашен.',
          items: [
            { link: '/assets', title: 'Активы' },
            { link: '#', title: 'Денежные требования' },
          ],
          iconType: 'assets',
        })
      );
  };

  return (
    <TableWrapper>
      <div className="realization">
        <div className="realization__header">
          {status && (
            <OrderRealizationsHeader
              onClose={onClose}
              status={status}
              showStatus
              title={orderRefNumber}
            />
          )}
          <OrderRealizationsDocs onSpecDownload={onSpecDownload} />
        </div>
        <OrderRealizationsSorts
          onItemToggle={onToggleSort}
          directions={directions}
        />
        <div className="realization__body">
          <OrderRealizationsList data={data} />
        </div>
        {sum && status && (
          <OrderRealizationsFooter
            isError={isError}
            errorText="Недостаточно средств для оплаты!"
            positionsCount={data.length}
            showAction={getFooterShowingStatus()}
            isInitiator={isInitiator}
            onSubmit={() => onSubmit()}
            sum={sum}
            status={status}
          />
        )}
        {!isInitiator && status === 'WAITING_FOR_SHIPMENT' && (
          <FormFooter>
            <div className="flex justify-end" style={{ width: '100%' }}>
              <div className="form-assets__flex">
                <div
                  className="form-assets__item form-assets__item--select"
                  style={{ width: 350 }}
                >
                  <UiSelect
                    label="Выберите подпись:"
                    value={selectedCertificate}
                    options={certificatesList}
                    onChange={onUserSelectCertificate}
                  />
                </div>
              </div>
              <div className="form-assets__item">
                <div className="form-item">
                  <IconBtn
                    onClick={handleSubmit}
                    disabled={!selectedCertificate}
                    icon={<SignIcon />}
                    title="Подтвердить отгрузку и погасить товарное требование"
                  />
                </div>
              </div>
            </div>
          </FormFooter>
        )}
      </div>
    </TableWrapper>
  );
};

export default OrderRealizations;
